import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    Eye, Plus, Trash2, Bell, BellOff, Loader2, AlertTriangle,
    ChevronDown, ChevronUp, ArrowDownLeft, ArrowUpRight, ArrowLeftRight,
    RefreshCw, ExternalLink, X
} from 'lucide-react';
import { watchlistService } from '../services/api';
import { toast } from 'sonner';
import { clsx } from 'clsx';
import { safeStr } from '../utils/safeStr';

function workbenchHref(chain: string, address: string) {
    const params = new URLSearchParams({ chain, address });
    return `/workbench?${params.toString()}`;
}

function caseHref(caseId: string) {
    return `/cases/${encodeURIComponent(caseId)}`;
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface AlertCriteria {
    notify_on_any_tx?: boolean;
    min_value_usdt?: number | null;
    directions?: string[];
    create_case?: boolean;
}

interface WatchlistEntry {
    id: number;
    address: string;
    chain: string;
    label: string | null;
    notes: string | null;
    is_active: boolean;
    alert_enabled: boolean;
    alert_criteria: AlertCriteria | null;
    alert_email: string | null;
    alert_chain_status: string | null;
    last_checked: string | null;
    added_at: string;
    case_id: string | null;
}

interface AlertEvent {
    id: number;
    watchlist_id: number;
    address: string;
    chain: string;
    tx_hash: string;
    counterparty_address: string | null;
    direction: string;
    value_raw: string | null;
    value_usdt: number | null;
    token_symbol: string | null;
    triggered_at: string;
    email_sent: boolean;
    case_id: string | null;
    investigation_id: string | null;
}

const SUPPORTED_CHAINS = ['TRX', 'ETH', 'MATIC', 'ARB', 'BASE', 'OP', 'AVAX', 'BNB', 'BTC'];

const CHAIN_COLORS: Record<string, string> = {
    TRX: 'text-red-400 bg-red-500/10 border-red-500/30',
    ETH: 'text-blue-400 bg-blue-500/10 border-blue-500/30',
    MATIC: 'text-primary bg-primary/10 border-primary/30',
    ARB: 'text-sky-400 bg-sky-500/10 border-sky-500/30',
    BASE: 'text-blue-300 bg-blue-400/10 border-blue-400/30',
    OP: 'text-rose-400 bg-rose-500/10 border-rose-500/30',
    AVAX: 'text-orange-400 bg-orange-500/10 border-orange-500/30',
    BNB: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
    BTC: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
};

// ─── Add Alert Form ───────────────────────────────────────────────────────────

interface AddAlertFormProps {
    onAdd: () => void;
    onCancel: () => void;
}

function AddAlertForm({ onAdd, onCancel }: AddAlertFormProps) {
    const [address, setAddress] = useState('');
    const [chain, setChain] = useState('TRX');
    const [label, setLabel] = useState('');
    const [alertEmail, setAlertEmail] = useState('');
    const [notifyOnAnyTx, setNotifyOnAnyTx] = useState(true);
    const [minValueUsdt, setMinValueUsdt] = useState('');
    const [directions, setDirections] = useState<string[]>(['inbound', 'outbound']);
    const [createCase, setCreateCase] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const toggleDirection = (d: string) => {
        setDirections(prev =>
            prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]
        );
    };

    const handleSubmit = async () => {
        if (!address.trim()) { toast.error('Address is required'); return; }
        if (directions.length === 0 && !notifyOnAnyTx) { toast.error('Select at least one direction'); return; }

        const criteria: AlertCriteria = {
            notify_on_any_tx: notifyOnAnyTx,
            min_value_usdt: minValueUsdt ? parseFloat(minValueUsdt) : null,
            directions: directions.length > 0 ? directions : ['inbound', 'outbound'],
            create_case: createCase,
        };

        setSubmitting(true);
        try {
            await watchlistService.add(
                address.trim(), chain,
                label.trim() || undefined,
                undefined,
                undefined,
                true,
                criteria,
                alertEmail.trim() || undefined
            );
            toast.success('Alert added successfully');
            onAdd();
        } catch (err: any) {
            toast.error(err?.response?.data?.detail || 'Failed to add alert');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 space-y-4">
            <div className="text-sm font-semibold text-primary">New Watchlist Alert</div>

            {/* Address + Chain */}
            <div className="grid grid-cols-[1fr_auto] gap-3">
                <div>
                    <label className="block text-xs text-muted-foreground mb-1">Address *</label>
                    <input
                        value={address}
                        onChange={e => setAddress(e.target.value)}
                        placeholder="Wallet address to monitor"
                        className="w-full rounded-lg border border-border/80 bg-secondary/30 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                    />
                </div>
                <div>
                    <label className="block text-xs text-muted-foreground mb-1">Chain *</label>
                    <select
                        value={chain}
                        onChange={e => setChain(e.target.value)}
                        className="rounded-lg border border-border/80 bg-secondary/30 px-3 py-2 text-sm text-slate-100 focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                    >
                        {SUPPORTED_CHAINS.map(c => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                        <option value="SOL">SOL — email alerts only (on-chain check limited)</option>
                    </select>
                </div>
            </div>

            {/* Label */}
            <div>
                <label className="block text-xs text-muted-foreground mb-1">Label (optional)</label>
                <input
                    value={label}
                    onChange={e => setLabel(e.target.value)}
                    placeholder="e.g. Suspect wallet, Exchange hot wallet"
                    className="w-full rounded-lg border border-border/80 bg-secondary/30 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                />
            </div>

            {/* Alert Email Override */}
            <div>
                <label className="block text-xs text-muted-foreground mb-1">Alert Email (optional — overrides account email)</label>
                <input
                    value={alertEmail}
                    onChange={e => setAlertEmail(e.target.value)}
                    placeholder="officer@agency.gov.in"
                    type="email"
                    className="w-full rounded-lg border border-border/80 bg-secondary/30 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                />
            </div>

            {/* Alert Criteria */}
            <div className="rounded-lg border border-border/80 bg-secondary/30/60 p-4 space-y-3">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Alert Criteria</div>

                {/* Notify on any tx */}
                <label className="flex items-center gap-3 cursor-pointer">
                    <button
                        type="button"
                        onClick={() => setNotifyOnAnyTx(v => !v)}
                        className={clsx(
                            'relative w-9 h-5 rounded-full transition-colors',
                            notifyOnAnyTx ? 'bg-primary' : 'bg-slate-700'
                        )}
                    >
                        <span className={clsx(
                            'absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform',
                            notifyOnAnyTx ? 'translate-x-4' : 'translate-x-0'
                        )} />
                    </button>
                    <span className="text-sm text-foreground/80">Notify on any transaction</span>
                </label>

                {/* Min value */}
                <div>
                    <label className="block text-xs text-muted-foreground mb-1">Minimum value (USDT) — leave blank for any amount</label>
                    <div className="flex items-center gap-2">
                        <span className="text-muted-foreground text-sm">$</span>
                        <input
                            value={minValueUsdt}
                            onChange={e => setMinValueUsdt(e.target.value)}
                            placeholder="e.g. 1000"
                            type="number"
                            min="0"
                            className="w-36 rounded-lg border border-border/80 bg-muted px-3 py-1.5 text-sm text-slate-100 placeholder-slate-500 focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                        />
                        <span className="text-xs text-muted-foreground">USDT</span>
                    </div>
                </div>

                {/* Directions */}
                <div>
                    <div className="text-xs text-muted-foreground mb-2">Transaction directions</div>
                    <div className="flex gap-2">
                        {[
                            { value: 'inbound', label: 'Inbound', icon: ArrowDownLeft },
                            { value: 'outbound', label: 'Outbound', icon: ArrowUpRight },
                        ].map(({ value, label: lbl, icon: Icon }) => (
                            <button
                                key={value}
                                type="button"
                                onClick={() => toggleDirection(value)}
                                className={clsx(
                                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors',
                                    directions.includes(value)
                                        ? 'border-primary bg-primary/20 text-primary'
                                        : 'border-border/80 bg-muted text-muted-foreground hover:border-slate-600'
                                )}
                            >
                                <Icon className="w-3.5 h-3.5" />
                                {lbl}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Create case */}
                <label className="flex items-center gap-3 cursor-pointer">
                    <button
                        type="button"
                        onClick={() => setCreateCase(v => !v)}
                        className={clsx(
                            'relative w-9 h-5 rounded-full transition-colors',
                            createCase ? 'bg-primary' : 'bg-slate-700'
                        )}
                    >
                        <span className={clsx(
                            'absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform',
                            createCase ? 'translate-x-4' : 'translate-x-0'
                        )} />
                    </button>
                    <span className="text-sm text-foreground/80">Auto-create Case + Investigation when alert fires</span>
                </label>
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end">
                <button
                    onClick={onCancel}
                    className="px-4 py-2 text-sm text-muted-foreground hover:text-slate-200 border border-border/80 rounded-lg hover:border-slate-600 transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="flex items-center gap-2 px-4 py-2 text-sm bg-primary hover:bg-brand-gold-hover text-primary-foreground text-foreground rounded-lg transition-colors disabled:opacity-50"
                >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Add Alert
                </button>
            </div>
        </div>
    );
}

// ─── Entry Card ───────────────────────────────────────────────────────────────

interface EntryCardProps {
    entry: WatchlistEntry;
    onRemove: (address: string) => void;
    onSaved: () => void;
}

function EntryCard({ entry, onRemove, onSaved }: EntryCardProps) {
    const [expanded, setExpanded] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [saving, setSaving] = useState(false);
    const criteria = entry.alert_criteria;

    // Editable local state
    const [editEnabled, setEditEnabled] = useState(entry.alert_enabled ?? false);
    const [editEmail, setEditEmail] = useState(entry.alert_email || '');
    const [editMinValue, setEditMinValue] = useState(criteria?.min_value_usdt?.toString() || '');
    const [editNotifyAny, setEditNotifyAny] = useState(criteria?.notify_on_any_tx ?? false);
    const [editCreateCase, setEditCreateCase] = useState(criteria?.create_case ?? false);
    const [editDirections, setEditDirections] = useState<string[]>(criteria?.directions || ['inbound', 'outbound']);

    const handleSave = async () => {
        setSaving(true);
        try {
            await watchlistService.update(entry.address, {
                is_active: editEnabled,
                alert_enabled: editEnabled,
                alert_email: editEmail || null,
                alert_criteria: {
                    notify_on_any_tx: editNotifyAny,
                    min_value_usdt: editMinValue ? parseFloat(editMinValue) : null,
                    directions: editDirections,
                    create_case: editCreateCase,
                },
            });
            toast.success('Watchlist entry updated');
            onSaved();
        } catch {
            toast.error('Failed to update');
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async () => {
        if (!confirm(`Remove alert for ${entry.address}?`)) return;
        setRemoving(true);
        try {
            await watchlistService.remove(entry.address);
            onRemove(entry.address);
            toast.success('Alert removed');
        } catch {
            toast.error('Failed to remove alert');
            setRemoving(false);
        }
    };

    const chainClass = CHAIN_COLORS[entry.chain] || 'text-muted-foreground bg-muted border-border';
    const statusBadge = entry.alert_chain_status === 'unsupported'
        ? <span className="text-xs px-2 py-0.5 rounded-md bg-muted border border-border text-muted-foreground">limited chain support</span>
        : entry.alert_enabled
            ? <span className="text-xs px-2 py-0.5 rounded-md bg-primary/10 border border-primary/20 text-primary flex items-center gap-1"><Bell className="w-2.5 h-2.5" />alerting</span>
            : <span className="text-xs px-2 py-0.5 rounded-md bg-muted border border-border text-muted-foreground flex items-center gap-1"><BellOff className="w-2.5 h-2.5" />watch only</span>;

    return (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3">
                <Eye className="w-4 h-4 text-muted-foreground shrink-0" />

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={clsx('text-xs font-medium px-1.5 py-0.5 rounded border', chainClass)}>
                            {entry.chain}
                        </span>
                        <Link
                            to={workbenchHref(entry.chain, entry.address)}
                            className="text-xs text-foreground/80 font-mono truncate max-w-[280px] hover:text-primary hover:underline"
                            title="Open in Workbench"
                        >
                            {entry.address}
                        </Link>
                        {entry.label && <span className="text-xs text-muted-foreground">— {entry.label}</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                        {statusBadge}
                        {entry.last_checked && (
                            <span className="text-xs text-muted-foreground">
                                Last checked: {new Date(entry.last_checked).toLocaleString()}
                            </span>
                        )}
                        {entry.case_id && (
                            <Link to={caseHref(entry.case_id)} className="text-xs text-primary hover:underline">
                                Open case
                            </Link>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                    <button
                        onClick={() => setExpanded(v => !v)}
                        className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors"
                        title="View criteria"
                    >
                        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    <button
                        onClick={handleRemove}
                        disabled={removing}
                        className="p-1.5 text-muted-foreground hover:text-destructive rounded-md hover:bg-muted transition-colors"
                        title="Remove alert"
                    >
                        {removing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            {expanded && (
                <div className="border-t border-border px-4 py-3 bg-muted/20 space-y-3">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-xs">
                        <label className="flex items-center gap-2 text-muted-foreground cursor-pointer">
                            <input type="checkbox" checked={editEnabled} onChange={e => setEditEnabled(e.target.checked)}
                                className="w-3.5 h-3.5 rounded accent-primary" />
                            <span>Alerts {editEnabled ? 'on' : 'off'}</span>
                        </label>

                        <label className="flex items-center gap-2 text-muted-foreground cursor-pointer">
                            <input type="checkbox" checked={editNotifyAny} onChange={e => setEditNotifyAny(e.target.checked)}
                                className="w-3.5 h-3.5 rounded accent-primary" />
                            <span>Notify on any tx</span>
                        </label>

                        <label className="flex items-center gap-2 text-muted-foreground cursor-pointer">
                            <input type="checkbox" checked={editCreateCase} onChange={e => setEditCreateCase(e.target.checked)}
                                className="w-3.5 h-3.5 rounded accent-primary" />
                            <span>Create case on alert</span>
                        </label>

                        <div className="flex items-center gap-2 text-muted-foreground">
                            <span>Min value:</span>
                            <input type="number" value={editMinValue} onChange={e => setEditMinValue(e.target.value)}
                                placeholder="0" min="0" step="any"
                                className="w-24 px-1.5 py-0.5 text-xs bg-background border border-border rounded text-foreground focus:ring-2 focus:ring-primary/20" />
                            <span className="text-muted-foreground">USDT</span>
                        </div>

                        <div className="flex items-center gap-1.5 text-muted-foreground">
                            <span>Directions:</span>
                            {['inbound', 'outbound'].map(d => (
                                <button
                                    key={d}
                                    type="button"
                                    onClick={() => setEditDirections(prev =>
                                        prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]
                                    )}
                                    className={clsx(
                                        'px-1.5 py-0.5 rounded border text-[10px]',
                                        editDirections.includes(d)
                                            ? 'bg-primary/10 border-primary/30 text-primary'
                                            : 'bg-muted border-border text-muted-foreground'
                                    )}
                                >
                                    {d}
                                </button>
                            ))}
                        </div>

                        <div className="col-span-2 flex items-center gap-2 text-muted-foreground">
                            <span>Alert email:</span>
                            <input type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)}
                                placeholder="email@example.com"
                                className="flex-1 px-2 py-0.5 text-xs bg-background border border-border rounded text-foreground focus:ring-2 focus:ring-primary/20" />
                        </div>

                        {entry.case_id && (
                            <div className="col-span-2 text-muted-foreground">
                                Linked case:{' '}
                                <Link to={caseHref(entry.case_id)} className="text-primary hover:underline font-mono text-[11px]">
                                    {entry.case_id}
                                </Link>
                            </div>
                        )}
                    </div>
                    <div className="flex justify-end pt-2 border-t border-border">
                        <button
                            onClick={handleSave}
                            disabled={saving || editDirections.length === 0}
                            className="px-3 py-1 text-xs font-medium bg-primary hover:bg-brand-gold-hover text-primary-foreground rounded transition-colors disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Alert Event Row ──────────────────────────────────────────────────────────

function AlertEventRow({ event }: { event: AlertEvent }) {
    const dirIcon = event.direction === 'inbound'
        ? <ArrowDownLeft className="w-3.5 h-3.5 text-muted-foreground" />
        : event.direction === 'outbound'
            ? <ArrowUpRight className="w-3.5 h-3.5 text-muted-foreground" />
            : <ArrowLeftRight className="w-3.5 h-3.5 text-muted-foreground" />;

    const chainClass = CHAIN_COLORS[event.chain] || 'text-muted-foreground bg-muted border-border';

    const explorerUrl = (() => {
        if (event.chain === 'TRX') return `https://tronscan.org/#/transaction/${event.tx_hash}`;
        if (event.chain === 'BTC') return `https://mempool.space/tx/${event.tx_hash}`;
        return `https://etherscan.io/tx/${event.tx_hash}`;
    })();

    return (
        <div className="grid grid-cols-[auto_auto_1fr_auto_auto_auto] gap-3 items-center px-4 py-2.5 border-b border-border hover:bg-muted/30 transition-colors">
            <span className={clsx('text-[11px] font-medium px-1.5 py-0.5 rounded border', chainClass)}>
                {event.chain}
            </span>
            <span title={event.direction} className="flex items-center">{dirIcon}</span>
            <div className="min-w-0">
                <Link
                    to={workbenchHref(event.chain, event.address)}
                    className="text-xs text-foreground/80 font-mono truncate block max-w-full hover:text-primary hover:underline"
                >
                    {event.address}
                </Link>
                {event.counterparty_address && (
                    <code className="text-[10px] text-muted-foreground font-mono truncate block max-w-full">
                        ↔ {event.counterparty_address}
                    </code>
                )}
                <div className="flex flex-wrap gap-2 mt-0.5">
                    {event.case_id && (
                        <Link to={caseHref(event.case_id)} className="text-[10px] text-primary hover:underline">
                            Case
                        </Link>
                    )}
                    {event.investigation_id && event.case_id && (
                        <Link
                            to={`/cases/${encodeURIComponent(event.case_id)}?investigation=${encodeURIComponent(event.investigation_id)}`}
                            className="text-[10px] text-primary hover:underline"
                        >
                            Investigation
                        </Link>
                    )}
                    {event.email_sent && (
                        <span className="text-[10px] text-muted-foreground">Email sent</span>
                    )}
                </div>
            </div>
            <div className="text-right shrink-0">
                {event.value_usdt != null && (
                    <span className="text-xs font-medium text-foreground">
                        ${event.value_usdt.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </span>
                )}
                {event.token_symbol && (
                    <span className="text-[10px] text-muted-foreground ml-1">{safeStr(event.token_symbol)}</span>
                )}
            </div>
            <div className="text-xs text-muted-foreground shrink-0 whitespace-nowrap">
                {new Date(event.triggered_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
            <a
                href={explorerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 text-muted-foreground hover:text-primary transition-colors"
                title="View on explorer"
            >
                <ExternalLink className="w-3.5 h-3.5" />
            </a>
        </div>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function WatchlistAlerts() {
    const [searchParams] = useSearchParams();
    const [entries, setEntries] = useState<WatchlistEntry[]>([]);
    const [events, setEvents] = useState<AlertEvent[]>([]);
    const [loadingEntries, setLoadingEntries] = useState(true);
    const [loadingEvents, setLoadingEvents] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [checkingNow, setCheckingNow] = useState(false);
    const [tab, setTab] = useState<'alerts' | 'events'>(
        searchParams.get('tab') === 'events' ? 'events' : 'alerts',
    );

    useEffect(() => { loadEntries(); loadEvents(); }, []);

    const loadEntries = async () => {
        setLoadingEntries(true);
        try {
            const data = await watchlistService.getAll();
            setEntries(data);
        } catch {
            toast.error('Failed to load watchlist');
        } finally {
            setLoadingEntries(false);
        }
    };

    const loadEvents = async () => {
        setLoadingEvents(true);
        try {
            const data = await watchlistService.getAlertEvents(100);
            setEvents(data.events || []);
        } catch {
            toast.error('Failed to load alert events');
        } finally {
            setLoadingEvents(false);
        }
    };

    const handleManualCheck = async () => {
        setCheckingNow(true);
        try {
            const result = await watchlistService.triggerAlertCheck();
            toast.success(`Check complete — ${result.checked ?? 0} addresses checked, ${result.fired ?? 0} alerts fired`);
            await Promise.all([loadEvents(), loadEntries()]);
        } catch {
            toast.error('Alert check failed — try again or check Data Sources if providers are down');
        } finally {
            setCheckingNow(false);
        }
    };

    const alertEnabledEntries = entries.filter(e => e.alert_enabled);
    const watchOnlyEntries = entries.filter(e => !e.alert_enabled);

    return (
        <div className="flex flex-col bg-background">
            {/* Header */}
            <div className="border-b border-border px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                            <Eye className="w-6 h-6 text-primary" />
                            Watchlist & Alerts
                        </h1>
                        <p className="text-sm text-muted-foreground mt-0.5">
                            Monitor wallets. When an alert fires, open Workbench or the linked case from here.
                        </p>
                        <div className="flex gap-4 mt-2 text-[10px] text-muted-foreground">
                            <span><strong className="text-foreground">Watch</strong> = track address, no email</span>
                            <span><strong className="text-foreground">Alert</strong> = watch + email on matching transactions</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleManualCheck}
                            disabled={checkingNow}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground border border-border/80 rounded-lg hover:border-slate-600 hover:text-slate-200 transition-colors disabled:opacity-50"
                        >
                            <RefreshCw className={clsx('w-3.5 h-3.5', checkingNow && 'animate-spin')} />
                            {checkingNow ? 'Checking…' : 'Run Check Now'}
                        </button>
                        <button
                            onClick={() => setShowAddForm(v => !v)}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary hover:bg-brand-gold-hover text-primary-foreground rounded-lg transition-colors"
                        >
                            {showAddForm ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                            {showAddForm ? 'Cancel' : 'Add Alert'}
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mt-4">
                    {([
                        { key: 'alerts', label: `Active Alerts (${alertEnabledEntries.length})` },
                        { key: 'events', label: `Alert Events (${events.length})` },
                    ] as const).map(t => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={clsx(
                                'px-4 py-1.5 rounded-lg text-sm font-medium transition-colors',
                                tab === t.key
                                    ? 'bg-primary/10 text-primary'
                                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                            )}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                {/* Add form */}
                {showAddForm && (
                    <AddAlertForm
                        onAdd={() => { setShowAddForm(false); loadEntries(); }}
                        onCancel={() => setShowAddForm(false)}
                    />
                )}

                {tab === 'alerts' && (
                    <>
                        {loadingEntries ? (
                            <div className="flex items-center justify-center py-12 text-muted-foreground">
                                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                Loading watchlist…
                            </div>
                        ) : alertEnabledEntries.length === 0 && !showAddForm ? (
                            <div className="text-center py-16 text-muted-foreground">
                                <Bell className="w-10 h-10 mx-auto mb-3 opacity-30" />
                                <div className="text-sm">No active alerts</div>
                                <div className="text-xs mt-1">Click "Add Alert" to monitor a wallet address</div>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                                    Active Alerts — {alertEnabledEntries.length} addresses
                                </div>
                                {alertEnabledEntries.map(entry => (
                                    <EntryCard
                                        key={entry.id}
                                        entry={entry}
                                        onRemove={addr => setEntries(prev => prev.filter(e => e.address !== addr))}
                                        onSaved={loadEntries}
                                    />
                                ))}
                                {watchOnlyEntries.length > 0 && (
                                    <>
                                        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-5 mb-3">
                                            Watch Only (alerts disabled) — {watchOnlyEntries.length} addresses
                                        </div>
                                        {watchOnlyEntries.map(entry => (
                                            <EntryCard
                                                key={entry.id}
                                                entry={entry}
                                                onRemove={addr => setEntries(prev => prev.filter(e => e.address !== addr))}
                                                onSaved={loadEntries}
                                            />
                                        ))}
                                    </>
                                )}
                            </div>
                        )}
                    </>
                )}

                {tab === 'events' && (
                    <>
                        {loadingEvents ? (
                            <div className="flex items-center justify-center py-12 text-muted-foreground">
                                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                Loading events…
                            </div>
                        ) : events.length === 0 ? (
                            <div className="text-center py-16 text-muted-foreground">
                                <AlertTriangle className="w-10 h-10 mx-auto mb-3 opacity-30" />
                                <div className="text-sm">No alert events yet</div>
                                <div className="text-xs mt-1">Events appear here when transactions match your alert criteria</div>
                            </div>
                        ) : (
                            <div className="rounded-xl border border-border/80/50 bg-secondary/30/60 overflow-hidden">
                                <div className="grid grid-cols-[auto_auto_1fr_auto_auto_auto] gap-3 items-center px-4 py-2 bg-muted/40 border-b border-border/80/50 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                                    <span>Chain</span>
                                    <span>Dir</span>
                                    <span>Address</span>
                                    <span className="text-right">Value</span>
                                    <span>Time</span>
                                    <span></span>
                                </div>
                                {events.map(event => (
                                    <AlertEventRow key={event.id} event={event} />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
